import { escapeHtml, type CloudflareEmailMessage } from '@/utilities/cloudflareEmail'
import { renderBrandedEmail, renderEmailButton } from '@/utilities/emailTemplate'
import { getServerSideURL } from '@/utilities/getURL'

export const VEZELKAART_FROM_EMAIL = 'info@glutenvrijadvies.nl'
export const VEZELKAART_ADMIN_EMAIL = 'info@glutenvrijadvies.nl'

export const VEZELKAART_EMAIL_SUBJECT = 'Je vezelkaarten staan voor je klaar'
export const VEZELKAART_SUBMIT_LABEL = 'Stuur mij de vezelkaarten'

const SIGNATURE_NAME = 'Esmée Borgts'
const SIGNATURE_ROLE = 'Glutenvrije diëtist'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export type VezelkaartCard = {
  title: string
  fileName: string
  previewImage: string
}

export const VEZELKAART_CARDS: readonly VezelkaartCard[] = [
  {
    title: 'Vezelkaart Groenten',
    fileName: 'vezelkaart-groenten.pdf',
    previewImage: '/images/vezelkaarten/vezelkaart-groenten.jpg',
  },
  {
    title: 'Vezelkaart Fruit',
    fileName: 'vezelkaart-fruit.pdf',
    previewImage: '/images/vezelkaarten/vezelkaart-fruit.jpg',
  },
  {
    title: 'Vezelkaart Algemeen',
    fileName: 'vezelkaart-algemeen.pdf',
    previewImage: '/images/vezelkaarten/vezelkaart-algemeen.jpg',
  },
  {
    title: 'Vezelkaart Glutenvrij',
    fileName: 'vezelkaart-glutenvrij.pdf',
    previewImage: '/images/vezelkaarten/vezelkaart-glutenvrij.jpg',
  },
]

export function cardDownloadUrl(card: VezelkaartCard) {
  return `${getServerSideURL()}/downloads/${card.fileName}`
}

export type VezelkaartRequestInput = {
  firstName: string
  email: string
  newsletterOptIn: boolean
}

export function normalizeVezelkaartEmail(email: string) {
  return email.trim().toLowerCase()
}

export function validateVezelkaartRequest(input: Partial<VezelkaartRequestInput>) {
  const firstName = typeof input.firstName === 'string' ? input.firstName.trim() : ''
  const email = typeof input.email === 'string' ? normalizeVezelkaartEmail(input.email) : ''
  const newsletterOptIn = input.newsletterOptIn === true

  if (!firstName) {
    return { ok: false as const, error: 'Vul je voornaam in.' }
  }

  if (!EMAIL_REGEX.test(email)) {
    return { ok: false as const, error: 'Vul een geldig e-mailadres in.' }
  }

  return {
    ok: true as const,
    data: {
      firstName,
      email,
      newsletterOptIn,
    },
  }
}

export function buildVezelkaartEmail({
  email,
  firstName,
}: Pick<VezelkaartRequestInput, 'email' | 'firstName'>): CloudflareEmailMessage {
  const safeFirstName = escapeHtml(firstName)
  const introLine = 'Wat leuk dat je de vezelkaarten hebt aangevraagd.'

  const text = `Hallo ${firstName},

${introLine}

Via de onderstaande links kun je de vezelkaarten downloaden:

${VEZELKAART_CARDS.map((card) => `Download de ${card.title}: ${cardDownloadUrl(card)}`).join('\n')}

Je kunt de kaarten digitaal bewaren of uitprinten, zodat je ze gemakkelijk kunt gebruiken tijdens het boodschappen doen of bij het samenstellen van je maaltijden.

Met gezonde groet,
${SIGNATURE_NAME}
${SIGNATURE_ROLE}
www.glutenvrijadvies.nl`

  const html = renderBrandedEmail({
    preheader: VEZELKAART_EMAIL_SUBJECT,
    signature: SIGNATURE_NAME,
    signatureRole: SIGNATURE_ROLE,
    body: `
      <p style="margin:0 0 16px;">Hallo ${safeFirstName},</p>
      <p style="margin:0 0 16px;">${introLine}</p>
      <p style="margin:0 0 20px;">Via de onderstaande knoppen kun je de vezelkaarten downloaden:</p>
      ${VEZELKAART_CARDS.map((card) =>
        renderEmailButton({ href: cardDownloadUrl(card), label: `Download de ${card.title}` }),
      ).join('\n      ')}
      <p style="margin:20px 0 0;">Je kunt de kaarten digitaal bewaren of uitprinten, zodat je ze gemakkelijk kunt gebruiken tijdens het boodschappen doen of bij het samenstellen van je maaltijden.</p>
    `,
  })

  return {
    to: email,
    from: VEZELKAART_FROM_EMAIL,
    subject: VEZELKAART_EMAIL_SUBJECT,
    html,
    text,
  }
}

export function buildVezelkaartAdminEmail({
  email,
  firstName,
  newsletterOptIn,
  requestedAt,
}: VezelkaartRequestInput & { requestedAt: Date }): CloudflareEmailMessage {
  const newsletterLabel = newsletterOptIn ? 'Ja' : 'Nee'
  const requestedAtLabel = requestedAt.toLocaleString('nl-NL', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'Europe/Amsterdam',
  })

  const text = `Nieuwe vezelkaarten aanvraag

Voornaam: ${firstName}
E-mail: ${email}
Nieuwsbrief toestemming: ${newsletterLabel}
Aangevraagd op: ${requestedAtLabel}`

  const html = `
    <h1>Nieuwe vezelkaarten aanvraag</h1>
    <p><strong>Voornaam:</strong> ${escapeHtml(firstName)}</p>
    <p><strong>E-mail:</strong> ${escapeHtml(email)}</p>
    <p><strong>Nieuwsbrief toestemming:</strong> ${newsletterLabel}</p>
    <p><strong>Aangevraagd op:</strong> ${requestedAtLabel}</p>
  `

  return {
    to: VEZELKAART_ADMIN_EMAIL,
    from: VEZELKAART_FROM_EMAIL,
    subject: 'Nieuwe aanvraag vezelkaarten',
    html,
    text,
  }
}
